import { MediaMatcher } from '@angular/cdk/layout';
import { NgClass, NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, CUSTOM_ELEMENTS_SCHEMA, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Inject, Input, OnChanges, PLATFORM_ID, ViewChild } from '@angular/core';
import { CmsService } from 'src/_service/CmsService';
import { Swiper, SwiperOptions } from 'swiper/types';
import { CmsButtonComponent } from '../cms-button/cms-button.component';
import { CmsCardComponent } from '../cms-card/cms-card.component';
import { Slide, Slider } from 'src/_model/storyblock';
import { CmsImageComponent } from '../cms-image/cms-image.component';

@Component({
    selector: 'app-cms-swiper',
    templateUrl: './cms-swiper.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [NgClass, NgTemplateOutlet, CmsCardComponent, CmsButtonComponent, CmsImageComponent]
})

export class CmsSwiperComponent implements AfterViewInit, OnChanges {

  @ViewChild('swiperRef') private swiperRef;
  swiper?: Swiper;
  uniqueId: string = `swiper-${Math.random().toString(36).substr(2, 9)}`;

  @Input() imageHeight: number = 125;
  @Input() lightArrows: boolean = false;
  @Input() autoSlidesPerView: boolean = false;
  imageNoResize: boolean = false;

  private _slider: Slider
  @Input() set slider(value: Slider) {
    this._slider = value;
    if (this._slider?.image_height !== undefined && this._slider?.image_height !== null) {
      this.imageHeight = this._slider.image_height;
    }
    if (this._slider?.light_arrows !== undefined && this._slider?.light_arrows !== null) {
      this.lightArrows = this._slider.light_arrows;
    }
    if (this._slider?.auto_slides_per_view !== undefined && this._slider?.auto_slides_per_view !== null) {
      this.autoSlidesPerView = this._slider.auto_slides_per_view;
    }
    if (this._slider?.image_no_resize !== undefined && this._slider?.image_no_resize !== null) {
      this.imageNoResize = this._slider.image_no_resize;
    }
    this._slider.slides = this._slider.slides.filter(s => !s.hidden)
    this._slider.slides.forEach(slide=>{
      if(slide.title)
        slide['title_rendered']= this._cmsService.render_rt(slide.title)      
      
      //for backwards compatibility from storyblock -> ability to move to storyblock color variable
      if(slide.bg_color && slide.bg_color.startsWith('bg-'))
          slide.bg_color = slide.bg_color.replace('bg-', '')

    })
  };
  get slider() { return this._slider }

  isMobile: any;
  fixedBlock: Slide;

  get autoSlidesPerViewClasses(): string {
    if (this.autoSlidesPerView && this.slider?.no_of_slides) {
      return `auto-slides-per-view auto-slides-per-view-${this.slider.no_of_slides}`;
    }
    if (this.autoSlidesPerView) {
      return 'auto-slides-per-view';
    }
    return '';
  }

  options: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 0,
    pagination: {
      clickable: true,
      bulletClass: 'swiper-pagination-bullet ',
      bulletActiveClass: 'swiper-pagination-bullet-active'
    },
    breakpoints: {
      1024: {
        slidesPerView: 3,
        pagination: {
          clickable: false,
          bulletClass: 'swiper-pagination-bullets hidden'
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
      }
    },
    enabled: true,
    direction: 'horizontal',
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {
    this.resize();
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private elementRef: ElementRef,
    private media: MediaMatcher,
    private _cmsService: CmsService,
    private ref: ChangeDetectorRef) {

    this.isMobile = media.matchMedia('(max-width: 960px)').matches;
  }

  get_content(field) {
    return this._cmsService.get_content(field);
  }

  get_src_as_css(field, contain) {
    return this._cmsService.get_src_as_css(field, contain)
  }

  get_src(field, contain, height?, width?) {
    return this._cmsService.get_src(field, contain, height, width);
}

  get_alt(field) {
    return this._cmsService.get_alt(field);
  }

  resize() {
    this.isMobile = this.media.matchMedia('(max-width: 960px)').matches;
  }

  expandedSlidesCount() {
    if (this.autoSlidesPerView) {
      return 'auto';
    }
    let count = 1;
    if (this.slider.expand_on_desktop && parseInt(this.slider?.no_of_slides) > 1) {
      count = parseInt(this.slider.no_of_slides);
    } else if (this.slider.expand_on_desktop) {
      count = this.slider.slides.length;
    } else if(this.slider.slide_type === 'banner') {
      count=1.2;
    }else{
      count = 1;
    }
    return count;
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.autoSlidesPerView) {
      this.options.slidesPerView = 'auto';
    }
    
    this.swiperRef;
    const swiperEl = this.swiperRef.nativeElement;
    if(this.slider.slide_type === 'banner'){
      this.options.loop = true;
      this.options.centeredSlides = true;
      this.options.breakpoints[1024].loop = true;
      this.options.breakpoints[1024].centeredSlides = true;

    }
    this.options.breakpoints[1024].slidesPerView = this.expandedSlidesCount();

    const nav1024 = this.options.breakpoints[1024].navigation;
    if (nav1024 && typeof nav1024 === 'object') {
      nav1024.nextEl = `.${this.uniqueId}-next`;
      nav1024.prevEl = `.${this.uniqueId}-prev`;
    }

    //change breakpoint options when there is a fixed block
    if(!!this.fixedBlock){
      const breakpointoptions = this.options.breakpoints[1024];
      delete this.options.breakpoints[1024];
      this.options.breakpoints[1440] = breakpointoptions;
      const nav1440 = this.options.breakpoints[1440].navigation;
      if (nav1440 && typeof nav1440 === 'object') {
        nav1440.nextEl = `.${this.uniqueId}-next`;
        nav1440.prevEl = `.${this.uniqueId}-prev`;
      }
    }

    Object.assign(swiperEl, this.options);

    this.swiper = this.swiperRef.nativeElement.swiper;
    swiperEl.initialize();
  }


  ngOnChanges() {
    if (this.slider.fixed) {
      this.fixedBlock = this.slider.slides[0];
      if (this.fixedBlock.title)
        this.fixedBlock["title_rendered"] = this._cmsService.render_rt(this.fixedBlock.title)
      this.slider.slides.shift();
    }

  }



}
